import { logError, logEvent } from './utils/logger.js';
import type { Airgap, ConsentDetail } from './types.js';
import { flattenObject } from './utils/objectUtils.js';

const botRE =
  /\b(?:bot|spider|accessibility|ahrefssiteaudit|alwaysonline|anthropic-ai|backlink|bingpreview|brightedgeoncrawl|chatgpt-user|chrome-lighthouse|claude-web|cohere-ai|cohere-command|datadog|daumoa|facebookcatalog|facebookexternalhit|flipboardproxy|google favicon|google-extended|google-inspectiontool|google-notebooklm|googleother|gtmetrix|headlesschrome|ia_archiver|instagram|lighthouse|mediapartners-google|meta-externalads|meta-externalagent|meta-externalfetcher|meta-webindexer|ms-office|newrelic|opengraph|optiversal|petalsearch|pingdom|pinterest|quora|qwantify|security|skypeuripreview|slurp|snapchat|sogou|statuscake|tiktok|validator|webpagetest|whatsapp|yeti|crawl)\b/i;
const AIRGAP_READY_TIMEOUT_MS = 5000;

const waitForAirgap = (): Promise<Airgap> => {
  const airgapReadyOnLoadPromise: Promise<Airgap> = new Promise((resolve) => {
    if (window.airgap.getConsent) {
      resolve(window.airgap);
    }
  });

  const timerPromise: Promise<Airgap> = new Promise((_, reject) =>
    setTimeout(() => {
      reject('consent-script-not-ready');
    }, AIRGAP_READY_TIMEOUT_MS),
  );

  const airgapReadyEventPromise: Promise<Airgap> = new Promise((resolve) => {
    if (window.airgap.ready) {
      window.airgap.ready((airgap) => resolve(airgap));
    }
  });

  return Promise.race([
    airgapReadyEventPromise,
    timerPromise,
    airgapReadyOnLoadPromise,
  ]);
};

const registerConsentChangeEventListener = (airgap: Airgap) => {
  if (!airgap?.addEventListener) {
    logError('consent-script-missing-listener', {});
    return;
  }
  airgap.addEventListener('consent-change', (event: Event) => {
    const { detail } = event as CustomEvent<ConsentDetail>;
    const flatDetail = flattenObject(
      detail as unknown as Record<string, unknown>,
    );
    logEvent('consent-change', flatDetail);
  });
};

const reportDefaultConsent = (airgap: Airgap) => {
  const flatDetail = flattenObject({
    consent: airgap.getConsent && airgap.getConsent(),
    signals: airgap.getPrivacySignals && airgap.getPrivacySignals(),
  });
  logEvent('get-consent', flatDetail);
};

const ensureAirgapReadyQueue = () => {
  if (!window?.airgap?.ready) {
    window.airgap = {
      readyQueue: [],
      ready(callback: (airgap: Airgap) => void): void {
        this.readyQueue?.push(callback);
      },
      ...window?.airgap,
    };
  }
};

async function main(): Promise<Airgap> {
  // skipping hasAirgapScript check as on 3.0 although airgap.js is not present airgap variable works well.
  ensureAirgapReadyQueue();
  return waitForAirgap();
}

if (
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  !botRE.test(navigator.userAgent)
) {
  main()
    .then((airgap: Airgap) => {
      reportDefaultConsent(airgap);
      registerConsentChangeEventListener(airgap);
    })
    .catch((error) => {
      try {
        const err = error instanceof Error ? error : new Error(String(error));
        logError(err.message, {
          message: err.message,
          stack: err.stack,
        });
      } catch (error) {
        console.error('[consent-logger] global try-catch-catch', error);
      }
    });
}
